import { useState } from "react";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { api } from "@/lib/api";
import { CEMETERIES, BLESSING_DATES, DONATION_AMOUNTS, peso } from "@/data/tombBlessing";

const blank = {
  requester_name: "",
  contact: "",
  email: "",
  cemetery: "",
  tomb_location: "",
  blessing_date: "",
  blessing_time: "",
  deceased_names: [""],
  donation_choice: "",
  donation_other: "",
  notes: "",
};

const Label = ({ children, required }) => (
  <label className="text-[10px] uppercase tracking-[0.28em] text-sage-dark font-sans block mb-2">
    {children}
    {required && <span className="text-terracotta ml-1">*</span>}
  </label>
);

const chip = (active) =>
  `border px-4 py-3 text-left transition-colors duration-300 ${
    active ? "border-terracotta bg-terracotta/[0.07]" : "border-parchment-400 bg-parchment-50 hover:border-cocoa-900/40"
  }`;

function validate(f, amount) {
  if (!f.requester_name.trim()) return "Please enter your name.";
  if (!f.contact.trim()) return "Please enter a contact number.";
  if (!f.cemetery) return "Please choose a cemetery.";
  if (!f.blessing_date) return "Please choose the date of blessing.";
  if (!f.blessing_time) return "Please enter the time of blessing.";
  if (f.blessing_time < "07:00" || f.blessing_time > "22:00") return "Time must be between 7:00 AM and 10:00 PM.";
  if (!f.deceased_names.some((n) => n.trim())) return "Please enter at least one name of the deceased.";
  if (!(amount > 0)) return "Please choose or enter a donation amount.";
  return null;
}

export default function TombBlessingForm({ onDone }) {
  const [f, setF] = useState(blank);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const amount = f.donation_choice === "other" ? Number(f.donation_other) : Number(f.donation_choice);

  const setName = (i, v) =>
    setF((s) => ({ ...s, deceased_names: s.deceased_names.map((n, j) => (j === i ? v : n)) }));
  const addName = () => setF((s) => ({ ...s, deceased_names: [...s.deceased_names, ""] }));
  const removeName = (i) =>
    setF((s) => ({ ...s, deceased_names: s.deceased_names.filter((_, j) => j !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    const err = validate(f, amount);
    if (err) return toast.error(err);
    setSending(true);
    try {
      const { donation_choice, donation_other, ...rest } = f;
      const res = await api.post("/tomb-blessings", { ...rest, email: f.email || null, donation_amount: amount });
      toast.success("Request received — eternal rest grant unto them, O Lord.");
      setF(blank);
      onDone(res.data);
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Could not submit. Please try again or call the parish office.");
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate data-testid="tomb-form" className="bg-parchment-50 border border-parchment-400 p-8 md:p-12 space-y-9">
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <Label required>Name of Requester</Label>
          <input data-testid="tomb-requester" className="field" value={f.requester_name} onChange={set("requester_name")} placeholder="Juan dela Cruz" />
        </div>
        <div>
          <Label required>Contact Number</Label>
          <input data-testid="tomb-contact" className="field" value={f.contact} onChange={set("contact")} placeholder="09XX XXX XXXX" />
        </div>
        <div>
          <Label>Email (optional)</Label>
          <input data-testid="tomb-email" type="email" className="field" value={f.email} onChange={set("email")} placeholder="name@example.com" />
        </div>
        <div>
          <Label>Tomb Location (optional)</Label>
          <input data-testid="tomb-location" className="field" value={f.tomb_location} onChange={set("tomb_location")} placeholder="e.g., Block 4, Lot 12 · near the chapel" />
        </div>
      </div>

      <div>
        <Label required>Cemetery</Label>
        <div className="grid sm:grid-cols-2 gap-3">
          {CEMETERIES.map((c, i) => (
            <button key={c} type="button" data-testid={`tomb-cemetery-${i}`} onClick={() => setF((s) => ({ ...s, cemetery: c }))} className={chip(f.cemetery === c)}>
              <span className="font-serif text-cocoa-900">{c}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-12 gap-6">
        <div className="md:col-span-8">
          <Label required>Date of Blessing</Label>
          <div className="grid grid-cols-3 gap-2 sm:gap-3">
            {BLESSING_DATES.map((d) => (
              <button key={d.value} type="button" data-testid={`tomb-date-${d.value}`} onClick={() => setF((s) => ({ ...s, blessing_date: d.value }))} className={`${chip(f.blessing_date === d.value)} !px-2.5 sm:!px-4 min-w-0`}>
                <div className="text-[10px] uppercase tracking-[0.2em] text-sage-dark">{d.day}</div>
                <div className="font-serif text-sm sm:text-lg text-cocoa-900 leading-tight break-words">{d.label}</div>
                <div className="text-[10px] text-terracotta mt-0.5 hidden sm:block">{d.tag}</div>
              </button>
            ))}
          </div>
        </div>
        <div className="md:col-span-4">
          <Label required>Time of Blessing</Label>
          <input data-testid="tomb-time" type="time" min="07:00" max="22:00" className="field text-lg" value={f.blessing_time} onChange={set("blessing_time")} />
          <p className="mt-2 text-[11px] text-cocoa-700 italic font-sans">Between 7:00 AM and 10:00 PM</p>
        </div>
      </div>

      <div>
        <Label required>Name(s) of the Deceased</Label>
        <div className="space-y-3">
          {f.deceased_names.map((n, i) => (
            <div key={i} className="flex items-center gap-3">
              <span className="font-serif text-terracotta w-6">{String(i + 1).padStart(2, "0")}</span>
              <input data-testid={`tomb-deceased-${i}`} className="field" value={n} onChange={(e) => setName(i, e.target.value)} placeholder="+ Full name of the departed" />
              {f.deceased_names.length > 1 && (
                <button type="button" aria-label="Remove name" data-testid={`tomb-deceased-remove-${i}`} onClick={() => removeName(i)} className="text-cocoa-700 hover:text-terracotta">
                  <X size={16} />
                </button>
              )}
            </div>
          ))}
        </div>
        <button type="button" data-testid="tomb-deceased-add" onClick={addName} className="mt-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-terracotta hover:text-cocoa-900 font-sans">
          <Plus size={14} /> Add another name
        </button>
      </div>

      <div>
        <Label required>Donation</Label>
        <div className="flex flex-wrap gap-3">
          {DONATION_AMOUNTS.map((a) => (
            <button key={a} type="button" data-testid={`tomb-donation-${a}`} onClick={() => setF((s) => ({ ...s, donation_choice: String(a) }))} className={`${chip(f.donation_choice === String(a))} rounded-full px-6 py-2.5`}>
              <span className="font-serif text-lg text-cocoa-900">{peso(a)}</span>
            </button>
          ))}
          <button type="button" data-testid="tomb-donation-other" onClick={() => setF((s) => ({ ...s, donation_choice: "other" }))} className={`${chip(f.donation_choice === "other")} rounded-full px-6 py-2.5`}>
            <span className="font-sans text-sm text-cocoa-900">Other amount</span>
          </button>
        </div>
        {f.donation_choice === "other" && (
          <div className="mt-4 max-w-xs flex items-baseline gap-2">
            <span className="font-serif text-xl text-cocoa-900">₱</span>
            <input data-testid="tomb-donation-other-input" type="number" min="1" className="field text-lg" value={f.donation_other} onChange={set("donation_other")} placeholder="Enter amount" />
          </div>
        )}
      </div>

      <div>
        <Label>Notes (optional)</Label>
        <textarea data-testid="tomb-notes" rows={2} className="field resize-y" value={f.notes} onChange={set("notes")} placeholder="Anything the priest should know (e.g., meeting point, family name on the tomb)." />
      </div>

      <button type="submit" disabled={sending} data-testid="tomb-submit" className="inline-flex items-center gap-3 bg-cocoa-900 text-parchment-100 px-8 py-4 font-sans text-sm tracking-wide hover:bg-terracotta transition-colors disabled:opacity-60">
        {sending ? "Sending…" : "Request Tomb Blessing"} <span>→</span>
      </button>
    </form>
  );
}
